import { XHRInstance, IListResponse, useXHR } from "@sembark-travel/xhr"
import { IUser, useAuthUser } from "../Auth"
import { Required } from "utility-types"
import { TFormattedMoney } from "@sembark-travel/money"
import { IGivenQuote } from "../Trips/store"
import { IInstalment } from "../Payments"
import useSWR from "swr"
import { IAccount } from "../Accounting"

export type TToken = {
	id: number
	name: string
	abilities: Array<string>
	last_used_at: string | null
	created_at: string
	updated_at: string | null
}

export type TIntegration = {
	id: number
	uid: string
	name: string
	description: string
	abilities: Array<string>
	created_at: string
	created_by?: IUser
	platform?: "google_ads" | "meta" | "api" | "pg_payu" | "pg_simulator"
	tokens?: Array<TToken> | null
	integrated_app_type: null | "fb_integrated_apps" | "pg_integrated_apps"
	integrated_app_id: null | number
	in_test_mode_at?: string
	paused_at?: string
	paused_by?: IUser
	google_ads_lead_form_webhook_config?: {
		url: string
		key: string
	}
}

export type TNewIntegrationSchema = {
	in_test_mode?: boolean | 0 | 1 | "0" | "1"
	name: string
	description: string
	abilities: Array<string>
	platform: Required<TIntegration>["platform"]
}

export type TPaymentMethod = {
	id: string
	name: string
}

export type TPGIntegratedApp = {
	id: number
	uid: string
	integration_uid: string
	payment_methods?: Array<TPaymentMethod>
	updated_at?: string
	bank_accounting_account?: IAccount
} & (
	| {
			pg_provider: "payu"
			payu: {
				merchant_id: string
			}
	  }
	| {
			pg_provider: "pg_simulator"
	  }
)

export type TFBIntegratedApp = {
	id: number
	app_id: string
	user_id: string
	integration_uid: string
	user_access_token_issued_at?: string
	webhook_token: string
	pages?: Array<TFBIntegratedPage>
}

export type TFBIntegratedPage = {
	id: number
	page_id: string
	name: string
	subscribed_at?: string
	access_token_issued_at?: string
	exception?: Record<string, unknown>
	forms_count?: number
}

export type TFBPageLead = {
	id: number
	leadgen_id: string
	page_id: string
	created_at: string
	webhook_at?: string
	webhook_payload?: Record<string, unknown>
	fetched_data?: Record<string, unknown>
	fetched_at?: string
	batch_id?: string
	form_id?: string
	ad_id?: string
	adgroup_id: string
	exception?: Record<string, unknown>
}

// actions
export function XHR(xhr: XHRInstance) {
	return {
		async store(data: TNewIntegrationSchema): Promise<{
			data: TIntegration
			meta?: {
				last_created_token: string
			}
		}> {
			return xhr.post("/integrations", data).then((resp) => resp.data)
		},
		async get(params: unknown): Promise<IListResponse<TIntegration>> {
			return xhr.get("/integrations", { params }).then((resp) => resp.data)
		},
		async show(integrationId: string | number): Promise<TIntegration> {
			return xhr
				.get(`/integrations/${integrationId}`)
				.then((resp) => resp.data.data)
		},
		async update(
			integrationId: string | number,
			payload: unknown
		): Promise<TIntegration> {
			return xhr
				.patch(`/integrations/${integrationId}`, payload)
				.then((resp) => resp.data.data)
		},
		async destroy(id: number): Promise<string | undefined> {
			return xhr
				.delete(`/integrations/${id}`)
				.then((resp) => resp.data?.data?.message)
		},
		async pause(id: number): Promise<string | undefined> {
			return xhr
				.post(`/integrations/${id}/pause`)
				.then((resp) => resp.data?.data?.message)
		},
		async resume(id: number): Promise<string | undefined> {
			return xhr
				.post(`/integrations/${id}/resume`)
				.then((resp) => resp.data?.data?.message)
		},
		async generateToken(id: number): Promise<{ token: string }> {
			return xhr
				.post(`/integrations/${id}/tokens`)
				.then((resp) => resp.data.data)
		},
		async destroyToken(
			integrationId: number,
			tokenId: number
		): Promise<string | undefined> {
			return xhr
				.delete(`/integrations/${integrationId}/tokens/${tokenId}`)
				.then((resp) => resp.data?.data?.message)
		},
	}
}

export type TPaymentGatewayIntegration = Required<
	Omit<TIntegration, "integrated_app_type">,
	"integrated_app_id"
> & { integrated_app_type: "pg_integrated_apps" }

export function isPaymentGatewayIntegration(
	integration: TIntegration
): integration is TPaymentGatewayIntegration {
	return Boolean(
		integration.integrated_app_id &&
			integration.integrated_app_type === "pg_integrated_apps"
	)
}

export type TPGInvoice = {
	id: number
	uid: string
	amount: TFormattedMoney
	link: string
	description: string
	is_active: boolean
	paid_at?: string
	status: string
	expiry_date: string
	created_at: string
	deleted_at?: string
	raw_data?: Record<string, unknown>
	payment_link?: TPGPaymentLink
	invoiceable_id: number
	successful_transactions?: Array<TPGTransaction>
	failed_transactions_count?: number
	unverified_successful_transactions_count?: number
	unverified_successful_transactions?: Array<TPGTransaction>
} & (
	| {
			invoiceable_type: "given_quotes"
			invoiceable: Required<IGivenQuote, "trip_quote">
	  }
	| {
			invoiceable_type: "instalments"
			invoiceable: Required<IInstalment, "payment">
	  }
)

export type TPGPaymentLink = {
	id: number
	uid: string
	amount: TFormattedMoney
	link: string
	description: string
	is_active: boolean
	status: string
	expiry_date: string
	created_at: string
	deleted_at?: string
	raw_data?: Record<string, unknown>
	invoice?: TPGInvoice
}

export type TPGTransaction = {
	id: number
	pg_provider_reference_id: string
	status: "success" | "failure"
	currency: string
	amount: TFormattedMoney
	rrn?: string
	arn?: string
	created_at: string
	date: string
	invoice?: TPGInvoice
	settlements?: Array<TPGSettlement>
	settlement_entries?: Array<TPGSettlementEntry>
	setl_txn_amount?: TFormattedMoney
	setl_fee?: TFormattedMoney
	setl_tax?: TFormattedMoney
	setl_date?: string
	setl_fee_inc_tax?: TFormattedMoney
	setl_credit_amount?: TFormattedMoney
	setl_debit_amount?: TFormattedMoney
	method?: string
	fee_and_tax_bifurcation?: TPGChargeBirfucation
	verified_at?: string
}

export type TPGSettlement = {
	id: number
	utr: string
	date: string
	transactions?: Array<TPGTransaction>
	entries?: Array<TPGSettlementEntry>
	request_amount?: TFormattedMoney
	txn_amount?: TFormattedMoney
	tax?: TFormattedMoney
	fee?: TFormattedMoney
	fee_inc_tax?: TFormattedMoney
	credit_amount?: TFormattedMoney
	debit_amount?: TFormattedMoney
	amount?: TFormattedMoney
	created_at: string
	fee_and_tax_bifurcation?: TPGChargeBirfucation
	unlinked_entries_count?: number
}

type TPGChargeBirfucation = {
	// customer is charged with following
	convenience_fee: TFormattedMoney
	convenience_tax: TFormattedMoney
	// merchant is charged with following
	merchant_fee: TFormattedMoney
	merchant_tax: TFormattedMoney
	// total
	total_fee: TFormattedMoney
	total_tax: TFormattedMoney
}

export type TPGSettlementEntry = {
	id: number
	pg_provider_transaction_reference_id: string
	currency: string
	txn_amount: TFormattedMoney
	txn_date: string
	txn_method: string
	fee: TFormattedMoney
	tax: TFormattedMoney
	fee_inc_tax: TFormattedMoney
	credit_amount: TFormattedMoney
	debit_amount: TFormattedMoney
	settlement?: TPGSettlement
	transaction?: TPGTransaction
	fee_and_tax_bifurcation?: TPGChargeBirfucation
}

export function usePaymentGatewayIntegratedAppsCount() {
	const { user } = useAuthUser()
	return user?.tenant?.pg_integrated_apps_count || 0
}

export function useFetchIntegration(id: number | string) {
	const xhr = useXHR()
	return useSWR(`/api/integrations/${id}`, () => XHR(xhr).show(id))
}

export function useFetchPaymentGatewayIntegratedApp(
	id: number | undefined | null | string
) {
	const xhr = useXHR()
	return useSWR(
		id ? `/api/pg/${id}` : null,
		() =>
			xhr
				.get<{
					data: TPGIntegratedApp
					meta: {
						exception?: Record<string, unknown>
						webhook_urls: Array<{
							name: string
							url: string
						}>
					}
				}>(`integrations/pg/${id}`)
				.then((resp) => resp.data),
		{ revalidateOnFocus: true }
	)
}
